/**
 * @name CarrinhoContext
 * @author Keven Adan Bezerra | kevenadanb
 * @description Context para gerenciar o carrinho de compras do totem
 * @package src/contexts/CarrinhoContext/CarrinhoContext.tsx
 */

import React, { createContext, useCallback, useMemo, useState } from "react";

// Types
export interface IProdutoCarrinho {
  id: number;
  nome: string;
  preco: number;
  quantidade: number;
  imagem?: string;
  observacoes?: string;
}

export interface ICarrinhoContext {
  // Estado do carrinho
  itens: IProdutoCarrinho[];
  total: number;
  quantidadeTotal: number;

  // Ações do carrinho
  adicionarItem: (
    produto: Omit<IProdutoCarrinho, "quantidade">,
    quantidade?: number,
  ) => void;
  removerItem: (produtoId: number) => void;
  atualizarQuantidade: (produtoId: number, novaQuantidade: number) => void;
  limparCarrinho: () => void;

  // Validações
  validarSaldo: (saldoDisponivel: number) => boolean;
  obterErroSaldo: (saldoDisponivel: number) => string | null;
}

// Context
export const CarrinhoContext = createContext<ICarrinhoContext>(
  {} as ICarrinhoContext,
);

// Provider Props
interface CarrinhoProviderProps {
  children: React.ReactNode;
}

/**
 * Provider do contexto de carrinho
 */
export const CarrinhoProvider: React.FC<CarrinhoProviderProps> = ({
  children,
}) => {
  const [itens, setItens] = useState<IProdutoCarrinho[]>([]);

  // Cálculos derivados
  const total = useMemo(() => {
    return itens.reduce((acc, item) => acc + item.preco * item.quantidade, 0);
  }, [itens]);

  const quantidadeTotal = useMemo(() => {
    return itens.reduce((acc, item) => acc + item.quantidade, 0);
  }, [itens]);

  // Adicionar item ao carrinho
  const adicionarItem = useCallback(
    (produto: Omit<IProdutoCarrinho, "quantidade">, quantidade = 1) => {
      setItens((current) => {
        const itemExistente = current.find((item) => item.id === produto.id);

        if (itemExistente) {
          // Atualiza quantidade do item existente
          return current.map((item) =>
            item.id === produto.id
              ? { ...item, quantidade: item.quantidade + quantidade }
              : item,
          );
        } else {
          // Adiciona novo item
          return [...current, { ...produto, quantidade }];
        }
      });
    },
    [],
  );

  // Remover item do carrinho
  const removerItem = useCallback((produtoId: number) => {
    setItens((current) => current.filter((item) => item.id !== produtoId));
  }, []);

  // Atualizar quantidade de um item
  const atualizarQuantidade = useCallback(
    (produtoId: number, novaQuantidade: number) => {
      if (novaQuantidade <= 0) {
        removerItem(produtoId);
        return;
      }

      setItens((current) =>
        current.map((item) =>
          item.id === produtoId
            ? { ...item, quantidade: novaQuantidade }
            : item,
        ),
      );
    },
    [removerItem],
  );

  // Limpar carrinho
  const limparCarrinho = useCallback(() => {
    setItens([]);
  }, []);

  // Validar se o saldo é suficiente
  const validarSaldo = useCallback(
    (saldoDisponivel: number) => {
      return saldoDisponivel >= total;
    },
    [total],
  );

  // Obter mensagem de erro de saldo
  const obterErroSaldo = useCallback(
    (saldoDisponivel: number) => {
      if (total === 0) return null;

      if (saldoDisponivel < total) {
        const diferenca = total - saldoDisponivel;
        return `Saldo insuficiente! Faltam R$ ${diferenca.toFixed(2)}`;
      }

      return null;
    },
    [total],
  );

  // Context value
  const contextValue: ICarrinhoContext = useMemo(
    () => ({
      itens,
      total,
      quantidadeTotal,
      adicionarItem,
      removerItem,
      atualizarQuantidade,
      limparCarrinho,
      validarSaldo,
      obterErroSaldo,
    }),
    [
      itens,
      total,
      quantidadeTotal,
      adicionarItem,
      removerItem,
      atualizarQuantidade,
      limparCarrinho,
      validarSaldo,
      obterErroSaldo,
    ],
  );

  return (
    <CarrinhoContext.Provider value={contextValue}>
      {children}
    </CarrinhoContext.Provider>
  );
};
